import React, { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Sparkles, X, RefreshCw, Check, Loader2 } from "lucide-react";

/**
 * AIGiftProposalDialog — Phase 9.
 *
 * Opens from the Gift Editor as an admin-approval gate for AI-generated gift
 * artwork. The dialog:
 *   1. Takes a subject prompt (e.g. "gold-encrusted taco with pink sparkles")
 *   2. POSTs to /admin/gifts/ai/generate — Nano Banana returns a proposal
 *   3. Renders the proposal image (with the previous artwork side-by-side
 *      for context) so admin can Reject / Regenerate / Approve & Save
 *   4. Approve calls the API which atomically attaches the new artwork to
 *      the gift. If the admin cancels, NOTHING is saved to the gift.
 *
 * Props:
 *   open      — boolean
 *   onClose   — () => void
 *   giftId    — required for approval; the AI attaches artwork to this gift
 *   previousImageUrl — string | null — previously approved artwork (audit ref)
 *   onApproved — (updatedGift) => void — parent refreshes the gift list
 */
export default function AIGiftProposalDialog({ open, onClose, giftId, previousImageUrl, onApproved }) {
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("transparent PNG, isometric, chunky gem materials");
  const [busy, setBusy] = useState(false);
  const [approving, setApproving] = useState(false);
  const [proposal, setProposal] = useState(null);
  // 'idle' | 'loading' | 'ok' | 'error' — used to gate Approve & Save on the
  // proposal image actually rendering in this browser. Without this the admin
  // could approve a broken image URL.
  const [imgState, setImgState] = useState("idle");
  const [errMsg, setErrMsg] = useState("");

  if (!open) return null;

  const resolveUrl = (u) => {
    if (!u) return null;
    if (u.startsWith("http")) return u;
    return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  };

  const generate = async () => {
    if (busy) return; // prevent double-tap
    if (!prompt.trim()) return toast.error("Describe what the gift should look like");
    setBusy(true);
    setErrMsg("");
    setImgState("idle");
    try {
      const { data } = await api.post("/admin/gifts/ai/generate", {
        prompt: prompt.trim(),
        style_hint: style.trim() || undefined,
        gift_id: giftId || undefined,
      }, { timeout: 90000 });
      if (!data?.image_url) {
        setErrMsg("The generator did not return an image. Try again.");
      } else {
        setImgState("loading");
        setProposal(data);
      }
    } catch (e) {
      const raw = e.response?.data?.detail;
      // FastAPI 422 returns an array of {msg, loc, ...} — flatten to a string
      let detail = null;
      if (typeof raw === "string") detail = raw;
      else if (Array.isArray(raw)) detail = raw.map((x) => x?.msg || JSON.stringify(x)).join("; ");
      else if (raw && typeof raw === "object") detail = raw.msg || JSON.stringify(raw);
      setErrMsg(detail || (e.code === "ECONNABORTED"
        ? "Generation timed out. Try again."
        : "We couldn't generate this image. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!proposal) return;
    try {
      await api.post(`/admin/gifts/ai/proposals/${proposal.proposal_id}/reject`);
    } catch (e) {
      // best effort — the important thing is we're not attaching it
    }
    setProposal(null);
    setImgState("idle");
    setErrMsg("");
  };

  const regenerate = async () => {
    // Reject current draft (best effort), clear preview, kick off a new one
    if (proposal) {
      api.post(`/admin/gifts/ai/proposals/${proposal.proposal_id}/reject`).catch(() => {});
    }
    setProposal(null);
    setImgState("idle");
    setErrMsg("");
    await generate();
  };

  const approve = async () => {
    if (!proposal) return;
    if (imgState !== "ok") return toast.error("Wait for the image to load before approving");
    if (!giftId) return toast.error("Save the gift first, then generate AI artwork.");
    setApproving(true);
    try {
      const { data } = await api.post(`/admin/gifts/ai/proposals/${proposal.proposal_id}/approve`, {
        gift_id: giftId,
      });
      toast.success("AI artwork approved and saved");
      onApproved?.(data.gift);
      setProposal(null);
      setImgState("idle");
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed — original artwork preserved");
    } finally {
      setApproving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[95] grid place-items-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="ai-gift-dialog"
    >
      <div className="w-full max-w-2xl rounded-3xl border border-pink-400/40 bg-zinc-950 p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-pink-300 font-black">
              <Sparkles className="w-3 h-3" /> AI-assisted gift artwork
            </p>
            <h3 className="text-xl font-black tracking-tight mt-0.5">Generate a new gift image</h3>
            <p className="text-xs text-zinc-400 mt-0.5">
              AI output is a proposal only. Nothing replaces the current artwork until you approve.
            </p>
          </div>
          <button
            onClick={() => { setProposal(null); onClose?.(); }}
            className="w-8 h-8 rounded-full grid place-items-center bg-white/5 hover:bg-white/10"
            aria-label="Close"
            data-testid="ai-gift-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {!proposal ? (
          <div className="space-y-3">
            {errMsg && (
              <div className="rounded-2xl border border-red-400/40 bg-red-500/10 p-3 text-xs text-red-200 flex items-start justify-between gap-3" data-testid="ai-gift-error-banner">
                <span>{errMsg}</span>
                <button
                  onClick={generate}
                  disabled={busy}
                  className="text-red-100 font-black underline underline-offset-2 shrink-0 disabled:opacity-50"
                  data-testid="ai-gift-retry"
                >
                  Retry
                </button>
              </div>
            )}
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                Describe the gift subject
              </label>
              <Input
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="A neon takeout box wrapped in a pink ribbon"
                className="h-11 bg-white/5 border-white/10 rounded-xl text-white"
                data-testid="ai-gift-prompt"
                maxLength={400}
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                Style hint (optional)
              </label>
              <Input
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                placeholder="transparent PNG, isometric, chunky gem materials"
                className="h-11 bg-white/5 border-white/10 rounded-xl text-white"
                data-testid="ai-gift-style"
                maxLength={120}
              />
              <p className="text-[10px] text-zinc-500">
                Hidden Gem always requests a transparent background and centered subject — this hint tunes the vibe.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                onClick={() => { setProposal(null); onClose?.(); }}
                className="h-9 px-4 rounded-full bg-white/5 border border-white/10 text-xs font-bold"
                data-testid="ai-gift-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={generate}
                disabled={busy}
                className="h-9 px-4 rounded-full bg-pink-500 hover:bg-pink-400 text-xs font-bold"
                data-testid="ai-gift-generate"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                {busy ? "Generating…" : "Generate proposal"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Current artwork</p>
                <div className="aspect-square rounded-xl bg-zinc-900 border border-white/10 grid place-items-center overflow-hidden">
                  {previousImageUrl ? (
                    <img
                      src={resolveUrl(previousImageUrl)}
                      alt="previous"
                      className="max-w-full max-h-full object-contain"
                      data-testid="ai-gift-current-img"
                    />
                  ) : (
                    <p className="text-xs text-zinc-500">No previous image</p>
                  )}
                </div>
              </div>
              <div className="rounded-2xl border border-pink-400/40 bg-pink-500/5 p-3">
                <p className="text-[10px] uppercase tracking-widest text-pink-300 font-bold mb-1.5">AI proposal</p>
                <div className="aspect-square rounded-xl bg-zinc-900 border border-pink-400/30 grid place-items-center overflow-hidden relative">
                  {imgState === "loading" && (
                    <div className="absolute inset-0 grid place-items-center text-zinc-500 text-xs" data-testid="ai-gift-proposal-loading">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  )}
                  {imgState === "error" && (
                    <div className="absolute inset-0 grid place-items-center text-center px-4 text-xs" data-testid="ai-gift-proposal-error">
                      <div>
                        <p className="text-red-300 font-bold">Image failed to load</p>
                        <p className="text-zinc-500 mt-1">The generator returned a broken image. Try Regenerate.</p>
                      </div>
                    </div>
                  )}
                  {/* Always mount the <img> so onLoad/onError fire even under error state */}
                  <img
                    key={proposal.proposal_id}
                    src={resolveUrl(proposal.image_url)}
                    alt="proposal"
                    onLoad={() => setImgState("ok")}
                    onError={() => setImgState("error")}
                    className={`max-w-full max-h-full object-contain transition-opacity ${imgState === "ok" ? "opacity-100" : "opacity-0"}`}
                    data-testid="ai-gift-proposal-img"
                  />
                </div>
              </div>
            </div>
            <div className="text-[11px] text-zinc-500 leading-relaxed">
              Not saved yet. Reject or Regenerate to try again — your current artwork stays in place until you tap Approve & Save.
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                onClick={reject}
                className="h-9 px-4 rounded-full bg-white/5 border border-white/10 text-xs font-bold"
                data-testid="ai-gift-reject"
              >
                <X className="w-3.5 h-3.5 mr-1.5" /> Reject
              </Button>
              <Button
                onClick={regenerate}
                disabled={busy}
                className="h-9 px-4 rounded-full bg-white/5 border border-white/10 text-xs font-bold"
                data-testid="ai-gift-regenerate"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                {busy ? "Regenerating…" : "Regenerate"}
              </Button>
              <Button
                onClick={approve}
                disabled={approving || !giftId || imgState !== "ok"}
                className="h-9 px-4 rounded-full bg-pink-500 hover:bg-pink-400 text-xs font-black disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="ai-gift-approve"
                title={imgState !== "ok" ? "Waiting for the image to load" : (!giftId ? "Save the gift first" : "")}
              >
                {approving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
                {approving ? "Saving…" : "Approve & Save"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
